import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ScriptService, Sound } from '../../../../service/script.service';

export interface SoundDialogData {
  sounds: Sound[];
}

export interface SoundDialogResult {
  selectedSound?: Sound;
}

@Component({
  selector: 'app-sound-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatSnackBarModule
  ],
  templateUrl: './sound-dialog.component.html',
  styleUrl: './sound-dialog.component.less'
})
export class SoundDialogComponent implements OnDestroy {
  sounds: Sound[] = [];
  currentlyPlaying: HTMLAudioElement | null = null;
  loadingSound: Sound | null = null;

  constructor(
    private scriptService: ScriptService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<SoundDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SoundDialogData
  ) {
    this.sounds = data.sounds || [];
  }

  onCancel(): void {
    this.stopCurrentSound();
    this.dialogRef.close();
  }

  playSound(sound: Sound): void {
    // Stop any currently playing sound
    this.stopCurrentSound();

    // Set loading state
    this.loadingSound = sound;

    // Create and play the new sound
    // The sound.url contains the direct URL to the audio file from the database
    const audio = new Audio("https://jar.games/assets/sounds/" + sound.url);
    
    audio.addEventListener('ended', () => {
      this.currentlyPlaying = null;
      this.loadingSound = null;
    });
    
    audio.addEventListener('canplaythrough', () => {
      this.loadingSound = null;
    });
    
    audio.play().then(() => {
      this.currentlyPlaying = audio;
    }).catch((error) => {
      console.error('Error playing sound:', error);
      this.loadingSound = null;
      this.snackBar.open('Error playing sound', 'Close', { duration: 3000 });
    });
  }

  stopCurrentSound(): void {
    if (this.currentlyPlaying) {
      this.currentlyPlaying.pause();
      this.currentlyPlaying.currentTime = 0;
      this.currentlyPlaying = null;
    }
    this.loadingSound = null;
  }

  copySoundName(sound: Sound): void {
    navigator.clipboard.writeText(sound.name).then(() => {
      this.snackBar.open(`"${sound.name}" copied to clipboard`, 'Close', { duration: 2000 });
    }).catch((error) => {
      console.error('Error copying to clipboard:', error);
      this.snackBar.open('Error copying to clipboard', 'Close', { duration: 3000 });
    });
  }

  onSoundClick(sound: Sound): void {
    this.playSound(sound);
    this.copySoundName(sound);
  }

  ngOnDestroy(): void {
    // Clean up any playing audio when component is destroyed
    this.stopCurrentSound();
  }

  isPlaying(sound: Sound): boolean {
    console.log('isPlaying', this.currentlyPlaying?.src, encodeURIComponent(sound.url));
    return this.currentlyPlaying?.src.includes(encodeURIComponent(sound.url)) ?? false;
  }

  isLoading(sound: Sound): boolean {
    return this.loadingSound === sound;
  }
}
